import { DateTime } from '@ksyos/date-time';
import { isEmpty } from 'lodash';
import { useState } from 'react';
import type { ITfoDiagnosticReportCustomControlData } from '@common/validation/customControls/ophthalmology';
import type {
    IAddendumAddEditValue,
    IValueFundusscreeningPolicyAndUrgency,
} from '@frontend/components/elements/AddendumAddEdit';
import type { IDiagnosticReportGetOne } from '@frontend/components/interfaces';
import { ProcessEntity, ServiceCode } from '@common/enums';
import { addAddendumTimeCheck } from '@common/utils/addAddendumTimeCheck';
import { Policies, tfV1DiagnosticReportAddendumAddSchema } from '@common/validation/customControls/ophthalmology';
import { FormProvider } from '@frontend/components/contexts/form/FormProvider';
import { AddendumAddEdit, AddendumAddEditValueInputType } from '@frontend/components/elements/AddendumAddEdit';
import { useAbortableFetch, useInterval } from '@frontend/utils';
import { tfoV1DiagnosticReportInitializeCustomControlData } from './TfoV1DiagnosticReportAddEditCustomControlFields';

interface ITfoV1DiagnosticReportAddendumAddProps {
    diagnosticReport: IDiagnosticReportGetOne & { customControlData: ITfoDiagnosticReportCustomControlData };
    onCancel: () => void;
    onSaved: () => void;
}

type TfoAddendumKeys = 'fundusscreeningPolicyAndUrgency' | 'retinopathieAndMaculopathie';

type TfoAddendumProperty = IAddendumAddEditValue & { key: TfoAddendumKeys };

export function TfoV1DiagnosticReportAddendumAdd(props: Readonly<ITfoV1DiagnosticReportAddendumAddProps>) {
    const customControlData: ITfoDiagnosticReportCustomControlData = tfoV1DiagnosticReportInitializeCustomControlData();

    const abortableFetch = useAbortableFetch();

    const [disableInput, setDisableInput] = useState(false);

    const [addendum, setAddendum] = useState<TfoAddendumProperty[]>([
        {
            key: 'retinopathieAndMaculopathie',
            value: {
                checked: undefined,
                customControlData,
                label: 'Retinopathie en maculopathie',
                type: AddendumAddEditValueInputType.retinopathieAndMaculopathieSelector,
            },
        },
        {
            key: 'fundusscreeningPolicyAndUrgency',
            value: {
                checked: undefined,
                customControlData,
                label: 'Geadviseerd beleid en urgentie',
                type: AddendumAddEditValueInputType.fundusscreeningPolicyAndUrgencyFields,
            },
        },
    ]);

    const dateSent = props.diagnosticReport.dateSent ? DateTime.from(props.diagnosticReport.dateSent) : undefined;

    const [allowAddAddendum, setAllowAddAddendum] = useState(addAddendumTimeCheck(dateSent));

    useInterval(
        () => {
            setAllowAddAddendum(addAddendumTimeCheck(dateSent));
        },
        allowAddAddendum ? 1000 : null
    );

    return (
        <FormProvider
            initialValues={props.diagnosticReport.customControlData}
            onSubmit={(formValues) => handleOnSave(addendum, formValues)}
            validationSchema={tfV1DiagnosticReportAddendumAddSchema}
        >
            {(form) => {
                checkGeadviseerdBeleidAndUrgentieIfRequired(form);

                return (
                    <AddendumAddEdit
                        disableCancel={disableInput}
                        disableInput={disableInput || !allowAddAddendum}
                        disableSend={disableInput || !allowAddAddendum}
                        onCancel={props.onCancel}
                        onChange={(addendumUpdate) => {
                            setAddendum(addendumUpdate);
                        }}
                        onSave={() => {
                            form.handleSubmit(null);
                        }}
                        value={addendum}
                    />
                );
            }}
        </FormProvider>
    );

    async function handleOnSave(
        properties: TfoAddendumProperty[],
        formValues: typeof props.diagnosticReport.customControlData
    ) {
        const checkedKeys = properties
            .map((property) => {
                if (property.value.checked) {
                    return property.key;
                }
            })
            .filter((key) => key);
        setDisableInput(true);
        setAddendum(properties);

        const saveProperties = getAddendumValueForKeys(checkedKeys as TfoAddendumKeys[], formValues);

        if (isEmpty(saveProperties)) {
            props.onCancel();
        }

        if (Object.keys(addendum).length > 0) {
            await abortableFetch.postJson(`/api/addendums`, {
                addendum: {
                    id: props.diagnosticReport.id,
                    processEntity: ProcessEntity.diagnosticReport,
                    properties: saveProperties,
                },
                serviceCode: ServiceCode.TFO,
            });
        }

        props.onSaved();
    }

    function getAddendumValueForKeys(
        keys: TfoAddendumKeys[],
        formValues: typeof props.diagnosticReport.customControlData
    ) {
        const addendumKeys = {
            fundusscreeningPolicyAndUrgency: ['geadviseerd_beleid', 'geadviseerd_beleid_anders', 'urgentie'],
            retinopathieAndMaculopathie: [
                'andere_bevindingen_od',
                'andere_bevindingen_od_text',
                'andere_bevindingen_os',
                'andere_bevindingen_os_text',
                'maculopathie_od',
                'maculopathie_os',
                'retinopathie_od',
                'retinopathie_os',
            ],
        };

        return keys.reduce((previousValues, currentKey) => {
            return addendumKeys[currentKey as keyof typeof addendumKeys].reduce((previous, customControlKey) => {
                const formValue = formValues[customControlKey as keyof ITfoDiagnosticReportCustomControlData];
                const customControlValue =
                    props.diagnosticReport.customControlData[
                        customControlKey as keyof ITfoDiagnosticReportCustomControlData
                    ];

                const parentChildFields = [
                    { child: 'andere_bevindingen_od_text', parent: 'andere_bevindingen_od' },
                    { child: 'andere_bevindingen_os_text', parent: 'andere_bevindingen_os' },
                    { child: 'geadviseerd_beleid_anders', parent: 'geadviseerd_beleid' },
                    { child: 'urgentie', parent: 'geadviseerd_beleid' },
                ];

                if (formValue !== customControlValue) {
                    const parentChildField = parentChildFields.find((field) => field.child === customControlKey);
                    if (parentChildField) {
                        const didDeleteGeadviseerdBeleid =
                            parentChildField.parent === 'geadviseerd_beleid' &&
                            ![
                                Policies.anders,
                                Policies.controleVolgensRichtlijn,
                                Policies.verwijzingOogartsDoorHuisarts,
                                Policies.verwijzingOogartsDoorOptometrist,
                            ].includes(formValues[parentChildField.parent]);

                        if (didDeleteGeadviseerdBeleid || !formValues[parentChildField.parent]) {
                            return previous;
                        } else {
                            return {
                                ...previous,
                                [parentChildField.parent]:
                                    formValues[parentChildField.parent] ??
                                    props.diagnosticReport.customControlData[parentChildField.parent],
                                [customControlKey]: formValue,
                            };
                        }
                    }

                    return {
                        ...previous,
                        [customControlKey]: formValue,
                    };
                } else {
                    return previous;
                }
            }, previousValues);
        }, {});
    }

    function checkGeadviseerdBeleidAndUrgentieIfRequired(form: typeof props.diagnosticReport.customControlData) {
        if (form.values.geadviseerd_beleid !== props.diagnosticReport.customControlData.geadviseerd_beleid) {
            const index = addendum.findIndex((addendumItem) => addendumItem.key === 'fundusscreeningPolicyAndUrgency');
            if (addendum[index].value.checked === undefined) {
                const updatedAddendum = [...addendum];
                updatedAddendum[index] = {
                    ...addendum[index],
                    value: {
                        ...addendum[index].value,
                        checked: true,
                    } as IValueFundusscreeningPolicyAndUrgency,
                };
                setAddendum(updatedAddendum);
            }
        }
    }
}
